import { CampaignStatusBadge } from "@/components/campaign/campaign-status-badge";
import type { CampaignRecord } from "@/hooks/use-campaigns";
import { useUpdateCampaign } from "@/hooks/use-update-campaign";
import {
  getDaysBeforeCampaignExpires,
  getFlightLabel,
  isCampaignExpiringSoon,
} from "@/utils/campaigns";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Separator,
} from "@getgreenline/blaze-ui";
import {
  ArchiveIcon,
  CopyIcon,
  Ellipsis,
  Eye,
  InfoIcon,
  PauseIcon,
  PlayIcon,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CampaignPerformanceMetrics } from "./campaign-performance-metrics";
import { CampaignPlacementBadges } from "./campaign-placement-badges";
import { CloneCampaignDialog } from "./dialogs/clone-campaign-dialog";
import { EndAndArchiveCampaignDialog } from "./dialogs/end-archive-campaign-dialog";
import { SpendProgress } from "./spend-progress";

type CampaignListCardProps = {
  campaign: CampaignRecord;
};

function resumeCampaign(
  campaign: CampaignRecord,
  updateCampaign: ReturnType<typeof useUpdateCampaign>,
) {
  updateCampaign.mutate(
    {
      campaignId: campaign.id,
      active: true,
      status: "ACTIVE",
    },
    {
      onSuccess: () => {
        toast.success("Campaign resumed", {
          description: <>{campaign.name} was successfully resumed.</>,
        });
      },
      onError: (e) => {
        toast.error("Campaign was not successfully resumed", {
          description: (
            <>
              {campaign.name} was not successfully resumed. Error: {e.message}
            </>
          ),
        });
      },
    },
  );
}

function pauseCampaign(
  campaign: CampaignRecord,
  updateCampaign: ReturnType<typeof useUpdateCampaign>,
) {
  updateCampaign.mutate(
    {
      campaignId: campaign.id,
      active: false,
      status: "PAUSED",
    },
    {
      onSuccess: () => {
        toast.success("Campaign paused", {
          description: <>{campaign.name} was successfully paused.</>,
        });
      },
      onError: (e) => {
        toast.error("Campaign was not successfully paused", {
          description: (
            <>
              {campaign.name} was not successfully paused. Error: {e.message}
            </>
          ),
        });
      },
    },
  );
}

function archiveCampaign(
  campaign: CampaignRecord,
  updateCampaign: ReturnType<typeof useUpdateCampaign>,
) {
  updateCampaign.mutate(
    {
      campaignId: campaign.id,
      active: false,
      status: "ARCHIVED",
    },
    {
      onSuccess: () => {
        toast.success("Campaign archived", {
          description: <>{campaign.name} was successfully archived.</>,
        });
      },
      onError: (e) => {
        toast.error("Campaign was not successfully archived", {
          description: (
            <>
              {campaign.name} was not successfully archived. Error: {e.message}
            </>
          ),
        });
      },
    },
  );
}

function CampaignActionsDropdown({
  campaign,
  setEndArchiveDialogOpen,
  setCloneCampaignDialogShown,
  updateCampaign,
  onOpenChange,
}: CampaignListCardProps & {
  setEndArchiveDialogOpen: (open: boolean) => void;
  setCloneCampaignDialogShown: (open: boolean) => void;
  updateCampaign: ReturnType<typeof useUpdateCampaign>;
  onOpenChange: (dropdownOpen: boolean) => void;
}) {
  const isArchived = campaign.status == "ARCHIVED";
  const isPaused = campaign.status == "PAUSED";
  const isScheduled = campaign.status == "SCHEDULED";
  const isRunning = campaign.status == "ACTIVE";
  const isActive = isRunning || isScheduled || isPaused;
  const navigate = useNavigate();

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`${campaign.name} menu`}
          className="tw:h-9 tw:w-9 tw:cursor-pointer tw:hover:bg-accent! tw:hover:text-sky-500!"
        >
          <Ellipsis className="tw:h-4 tw:w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuGroup>
          {isPaused && (
            <DropdownMenuItem
              onClick={() => resumeCampaign(campaign, updateCampaign)}
              className="tw:cursor-pointer! tw:hover:bg-accent!"
            >
              <PlayIcon className="tw:size-4" />
              <span>Resume Campaign</span>
            </DropdownMenuItem>
          )}
          {isActive && (isRunning || isScheduled) && (
            <DropdownMenuItem
              onClick={() => pauseCampaign(campaign, updateCampaign)}
              className="tw:cursor-pointer! tw:hover:bg-accent!"
            >
              <PauseIcon className="tw:size-4" />
              <span>Pause Campaign</span>
            </DropdownMenuItem>
          )}
          {!isArchived && (
            <DropdownMenuItem
              className="tw:text-red-500 tw:cursor-pointer! tw:hover:bg-accent!"
              onClick={() => setEndArchiveDialogOpen(true)}
            >
              <ArchiveIcon className="tw:size-4" color="red" />
              {campaign.status === "ENDED" ? (
                <span>Archive campaign</span>
              ) : (
                <span>End and archive campaign</span>
              )}
            </DropdownMenuItem>
          )}
          {!isArchived ? <Separator className="tw:my-1.5" /> : undefined}
          <DropdownMenuItem
            onClick={() => {
              setCloneCampaignDialogShown(true);
            }}
            className="tw:cursor-pointer! tw:hover:bg-accent!"
          >
            <CopyIcon></CopyIcon>
            <span>Clone Campaign</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => navigate(`/campaigns/${campaign.id}`)}
            className="tw:cursor-pointer! tw:hover:bg-accent!"
          >
            <Eye className="tw:size-4" />
            <span>View Details</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CampaignListCard({ campaign }: CampaignListCardProps) {
  const navigate = useNavigate();
  const updateCampaign = useUpdateCampaign();
  const [cloneCampaignDialogShown, setCloneCampaignDialogShown] =
    useState(false);
  const [endArchiveDialogOpen, setEndArchiveDialogOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dialogWindowOpen = cloneCampaignDialogShown || endArchiveDialogOpen;

  return (
    <Card
      className="tw:w-full tw:border-border/70 tw:bg-card/95 tw:cursor-pointer"
      onClick={() => {
        if (dropdownOpen || dialogWindowOpen) return;
        navigate(`/campaigns/${campaign.id}`);
      }}
    >
      <CardHeader className="tw:justify-between tw:flex tw:row tw:gap-4 lg:tw:flex-row lg:tw:items-start lg:tw:justify-between">
        <div className="tw:min-w-0 tw:space-y-3">
          <div className="tw:flex tw:items-start tw:gap-3">
            <Avatar className="tw:h-8 tw:w-8 tw:shrink-0">
              <AvatarFallback className="tw:text-xs tw:font-semibold">
                {campaign.initials}
              </AvatarFallback>
            </Avatar>
            <div className="tw:min-w-0 tw:space-y-1">
              <CardTitle className="tw:text-md tw:font-semibold tw:leading-tight tw:capitalize">
                <h2>{campaign.name}</h2>
              </CardTitle>
              <div className="tw:flex tw:flex-wrap tw:gap-2">
                <p className="tw:text-sm tw:text-muted-foreground">
                  {campaign.market}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="tw:flex tw:items-center tw:gap-2">
          <CampaignStatusBadge status={campaign.status} />

          <div onClick={(event) => event.stopPropagation()}>
            <CampaignActionsDropdown
              campaign={campaign}
              setCloneCampaignDialogShown={setCloneCampaignDialogShown}
              setEndArchiveDialogOpen={setEndArchiveDialogOpen}
              updateCampaign={updateCampaign}
              onOpenChange={(isDropdownOpen) => setDropdownOpen(isDropdownOpen)}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="tw:space-y-3">
        <CampaignPerformanceMetrics
          spend={campaign.spend}
          impressions={campaign.impressionsLabel}
          ctr={campaign.ctrLabel}
        />
        <div className="tw:flex tw:gap-3 tw:flex-col tw:py-2">
          <p className="tw:text-sm tw:text-muted-foreground">Dates</p>
          <div className="tw:flex tw:gap-4">
            {getFlightLabel(campaign)}
            {isCampaignExpiringSoon(campaign) ? (
              <Badge variant="secondary">
                <InfoIcon />
                Campaign ending in {getDaysBeforeCampaignExpires(campaign)} days
              </Badge>
            ) : undefined}
          </div>
        </div>
        <div className="tw:flex tw:gap-3 tw:flex-col tw:py-2">
          <p className="tw:text-sm tw:text-muted-foreground">UI Zones</p>
          <CampaignPlacementBadges
            placements={campaign.placements}
            maxVisible={3}
          />
        </div>
        <SpendProgress
          spendMilliDollars={campaign.spendMilliDollars}
          budgetMilliDollars={campaign.budgetMilliDollars}
          spendLabel={campaign.spend}
          budgetLabel={campaign.budget}
        />
      </CardContent>
      {cloneCampaignDialogShown && (
        <CloneCampaignDialog
          campaign={campaign}
          open={cloneCampaignDialogShown}
          onOpenChange={setCloneCampaignDialogShown}
        />
      )}
      {endArchiveDialogOpen && (
        <EndAndArchiveCampaignDialog
          campaign={campaign}
          updateCampaign={() => archiveCampaign(campaign, updateCampaign)}
          onClose={() => setEndArchiveDialogOpen(false)}
        />
      )}
    </Card>
  );
}
